import { useAuth, useCustomer, useStagingMode } from "@middleware/hooks";
import { Content } from "@prismicio/client";
import React, { useEffect, useState } from "react";
import { getActiveBar, getActivePopup } from "@middleware/helpers";
import {
  BAR_BILAN_CODE,
  BAR_MAIL_CAPTURE_CODE,
  BAR_PROMO_BAR_CODE,
  BAR_REFERRAL_CODE,
  POPUP_MAIL_CAPTURE_CODE,
  POPUP_REFERRAL_CODE,
  RESULT_BILAN_DATA,
} from "@middleware/constants";
import { SliceZone } from "@prismicio/react";
import { components } from "slices";
import { useRouter } from "next/router";
import Api from "@middleware/api";
import { getFromStorage } from "@middleware/helpers/global/sessions";
import { TResultBilan } from "@middleware/types";
import MailCaptureBar from "./MailCaptureBar/MailCaptureBar";
import ReferralBar from "./ReferralBar/ReferralBar";
import { createClient } from "../../../../prismicio";
import BilanBar from "./BilanBar/BilanBar";

interface Props {
  disabledBars?: string[];
  additionalBar?: React.ReactNode;
}
export default function Bars({ disabledBars = [], additionalBar }: Props) {
  const [activeBar, setActiveBar] = useState<string>();
  const [activePopup, setActivePopup] = useState<string>();
  const router = useRouter();
  const { user } = useAuth();
  const [captureMailData, setCaptureMailData] = useState<
    Content.EmailCapturePopupDocument | Content.EmailCapturePopupStagingDocument
  >();
  const [referralBarData, setReferralBarData] = useState<
    Content.ReferralPopupDocument | Content.ReferralPopupStagingDocument
  >();
  const [bilanBarData, setBilanBarData] = useState<
    Content.BilanBarDocument | Content.BilanBarStagingDocument
  >();
  const [segments, setSegments] = useState<
    Content.SegmentsDocument | Content.SegmentsStagingDocument
  >();
  const [bilanPassed, setBilanPassed] = useState(true);
  const { customer } = useCustomer();
  const { isStagingMode } = useStagingMode();

  useEffect(() => {
    const getBars = async () => {
      const client = createClient();

      const [
        captureMailBar,
        referralBar,
        bilanBar,
        captureMailBarStaging,
        referralBarStaging,
        bilanBarStaging,
        segmentBar,
        segmentBarStaging,
        bilanData,
      ] = await Promise.all([
        client.getSingle("email_capture_popup"),
        client.getSingle("referral_popup"),
        client.getSingle("bilan_bar"),
        client.getSingle("email_capture_popup_staging"),
        client.getSingle("referral_popup_staging"),
        client.getSingle("bilan_bar_staging"),
        client.getSingle("segments"),
        client.getSingle("segments_staging"),
        Api.bilan.getResultBilan(user?.id),
      ]);
      setCaptureMailData(
        isStagingMode ? captureMailBarStaging : captureMailBar,
      );
      setBilanBarData(isStagingMode ? bilanBarStaging : bilanBar);
      setReferralBarData(isStagingMode ? referralBarStaging : referralBar);
      setSegments(isStagingMode ? segmentBarStaging : segmentBar);
      if (!bilanData) {
        const storedData = getFromStorage(RESULT_BILAN_DATA) as
          | TResultBilan
          | undefined;
        if (!storedData) {
          setBilanPassed(false);
        }
      }
    };
    void getBars();
  }, [isStagingMode, user]);

  useEffect(() => {
    if (
      segments === undefined ||
      captureMailData === undefined ||
      referralBarData === undefined ||
      bilanBarData === undefined
    )
      return;

    const captureMailActivation = captureMailData.data.activateOrDeactivate;
    const captureMailBarActivation =
      captureMailData.data.activateOrDeactivateBar;
    const referralFormActivation = referralBarData.data.activateOrDeactivate;
    const referralFormBarActivation =
      referralBarData.data.activateOrDeactivateBar;
    const bilanBarActivation = bilanBarData.data.activateOrDeactivate;
    const bar = getActiveBar(
      router,
      bilanPassed,
      bilanBarActivation,
      customer,
      captureMailBarActivation,
      referralFormBarActivation,
    );
    const popup = getActivePopup(
      customer,
      captureMailActivation,
      referralFormActivation,
    );

    const hasDisabledBar = bar !== undefined && !disabledBars.includes(bar);
    if (hasDisabledBar) {
      setActiveBar(bar);
    } else {
      setActiveBar(undefined);
    }
    if (hasDisabledBar || popup !== undefined) {
      setActivePopup(popup);
    } else {
      setActivePopup(undefined);
    }
  }, [
    captureMailData,
    referralBarData,
    bilanBarData,
    disabledBars,
    customer,
    router,
    bilanPassed,
    segments,
  ]);

  return (
    <>
      <MailCaptureBar
        mailCaptureData={captureMailData}
        barIsActivated={activeBar === BAR_MAIL_CAPTURE_CODE}
        popupIsActivated={activePopup === POPUP_MAIL_CAPTURE_CODE}
      />
      <ReferralBar
        referralBarData={referralBarData}
        barIsActivated={activeBar === BAR_REFERRAL_CODE}
        popupIsActivated={activePopup === POPUP_REFERRAL_CODE}
      />
      {activeBar === BAR_BILAN_CODE && <BilanBar bilanBarData={bilanBarData} />}

      <SliceZone
        slices={segments?.data.slices}
        components={components}
        context={{
          hidePromoBar: disabledBars.includes(BAR_PROMO_BAR_CODE),
          additionalBar,
        }}
      />
    </>
  );
}
